import { useState } from 'react';

// ─── tiny helpers ────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 8);

const Badge = ({ children, color = '#e8fdf2', textColor = '#128C7E', border = '#c3f0da' }) => (
  <span style={{
    fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
    background: color, color: textColor, border: `1px solid ${border}`
  }}>{children}</span>
);

const TRIGGERS = [
  { value: 'keyword', label: 'Keyword Match' },
  { value: 'first_message', label: 'First Message' },
  { value: 'campaign_reply', label: 'Campaign Reply' },
  { value: 'button_click', label: 'Button / List Click' },
  { value: 'webhook', label: 'Webhook Event' },
];

const RESPONSE_TYPES = [
  { value: 'text', label: 'Text Message', icon: '💬' },
  { value: 'list', label: 'Interactive List', icon: '≡' },
  { value: 'buttons', label: 'Reply Buttons', icon: '⬜' },
  { value: 'webhook', label: 'Webhook / API Call', icon: '🔗' },
];

const SAVE_TYPES = [
  { value: 'user_trait', label: 'User Trait', desc: 'Stored permanently on contact record' },
  { value: 'workflow_var', label: 'Workflow Variable', desc: 'Temporary — used only within this workflow' },
];

const TRAIT_OPTIONS = ['name', 'email', 'phone', 'city', 'company', 'custom...'];

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE'];

const emptyStep = () => ({
  id: uid(),
  responseType: 'text',
  message: '',
  saveResponse: false,
  saveType: 'user_trait',
  saveTrait: 'name',
  saveVarName: '',
  listItems: [{ id: uid(), title: '', desc: '' }],
  buttons: [{ id: uid(), label: '' }],
  webhookMethod: 'POST',
  webhookUrl: '',
});

const emptyWorkflow = () => ({
  id: uid(),
  name: '',
  trigger: 'keyword',
  keyword: '',
  active: false,
  steps: [emptyStep()],
});

// ─── Step Editor ─────────────────────────────────────────────────────────────
function StepEditor({ step, idx, onChange, onRemove, isLast }) {
  const update = (key, val) => onChange({ ...step, [key]: val });

  return (
    <div style={{
      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
      padding: 16, marginBottom: 12, position: 'relative'
    }}>
      {/* Step header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{
          width: 26, height: 26, borderRadius: '50%', background: '#128C7E', color: '#fff',
          fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>{idx + 1}</div>
        <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>Step {idx + 1}</span>
        {/* Response Type selector */}
        <div style={{ display: 'flex', gap: 4 }}>
          {RESPONSE_TYPES.map(rt => (
            <button key={rt.value} onClick={() => update('responseType', rt.value)}
              title={rt.label}
              style={{
                padding: '4px 8px', borderRadius: 6, border: '1.5px solid',
                fontSize: 12, cursor: 'pointer', fontWeight: step.responseType === rt.value ? 700 : 400,
                borderColor: step.responseType === rt.value ? '#25D366' : '#e0e0e0',
                background: step.responseType === rt.value ? '#e8fdf2' : '#fff',
                color: step.responseType === rt.value ? '#128C7E' : '#555',
              }}>{rt.icon} {rt.label}</button>
          ))}
        </div>
        {idx > 0 && (
          <button onClick={onRemove} style={{
            border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: '0 4px'
          }}>✕</button>
        )}
      </div>

      {/* Text Message */}
      {step.responseType === 'text' && (
        <textarea value={step.message} onChange={e => update('message', e.target.value)}
          placeholder="Type your message… Use {{customer_name}}, {{workflow_var}} etc."
          rows={3} style={{
            width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
            fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
          }} />
      )}

      {/* Interactive List */}
      {step.responseType === 'list' && (
        <div>
          <textarea value={step.message} onChange={e => update('message', e.target.value)}
            placeholder="List header message…" rows={2} style={{
              width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 8
            }} />
          {step.listItems.map((item, li) => (
            <div key={item.id} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input value={item.title} onChange={e => {
                const n = step.listItems.map((x, i) => i === li ? { ...x, title: e.target.value } : x);
                update('listItems', n);
              }} placeholder={`Option ${li + 1} title`} style={{
                flex: 2, padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13
              }} />
              <input value={item.desc} onChange={e => {
                const n = step.listItems.map((x, i) => i === li ? { ...x, desc: e.target.value } : x);
                update('listItems', n);
              }} placeholder="Description (optional)" style={{
                flex: 3, padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13
              }} />
              {step.listItems.length > 1 && (
                <button onClick={() => update('listItems', step.listItems.filter((_, i) => i !== li))}
                  style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14 }}>✕</button>
              )}
            </div>
          ))}
          {step.listItems.length < 10 && (
            <button onClick={() => update('listItems', [...step.listItems, { id: uid(), title: '', desc: '' }])}
              style={{ fontSize: 12, color: '#128C7E', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
              + Add option
            </button>
          )}
        </div>
      )}

      {/* Reply Buttons */}
      {step.responseType === 'buttons' && (
        <div>
          <textarea value={step.message} onChange={e => update('message', e.target.value)}
            placeholder="Button message…" rows={2} style={{
              width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 8
            }} />
          {step.buttons.map((btn, bi) => (
            <div key={btn.id} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input value={btn.label} onChange={e => {
                const n = step.buttons.map((x, i) => i === bi ? { ...x, label: e.target.value } : x);
                update('buttons', n);
              }} placeholder={`Button ${bi + 1} label`} maxLength={20} style={{
                flex: 1, padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13
              }} />
              {step.buttons.length > 1 && (
                <button onClick={() => update('buttons', step.buttons.filter((_, i) => i !== bi))}
                  style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14 }}>✕</button>
              )}
            </div>
          ))}
          {step.buttons.length < 3 && (
            <button onClick={() => update('buttons', [...step.buttons, { id: uid(), label: '' }])}
              style={{ fontSize: 12, color: '#128C7E', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
              + Add button (max 3)
            </button>
          )}
        </div>
      )}

      {/* Webhook */}
      {step.responseType === 'webhook' && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#92400e', marginBottom: 8 }}>
            ⚠️ Webhooks are available on Advanced & Enterprise plans only.
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <select value={step.webhookMethod} onChange={e => update('webhookMethod', e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13 }}>
              {HTTP_METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
            <input value={step.webhookUrl} onChange={e => update('webhookUrl', e.target.value)}
              placeholder="https://your-api.com/endpoint" style={{
                flex: 1, padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13
              }} />
          </div>
          <textarea value={step.message} onChange={e => update('message', e.target.value)}
            placeholder='Request body (JSON)… e.g. {"order_id": "{{workflow_var}}"}'
            rows={2} style={{
              width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace'
            }} />
        </div>
      )}

      {/* Save Response */}
      {step.responseType !== 'webhook' && (
        <div style={{ marginTop: 10, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={step.saveResponse} onChange={e => update('saveResponse', e.target.checked)} />
            <span style={{ fontWeight: 500 }}>Save customer response</span>
          </label>
          {step.saveResponse && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SAVE_TYPES.map(st => (
                <label key={st.value} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12,
                  padding: '6px 10px', border: `1.5px solid ${step.saveType === st.value ? '#25D366' : '#e0e0e0'}`,
                  borderRadius: 8, cursor: 'pointer', background: step.saveType === st.value ? '#e8fdf2' : '#fff'
                }}>
                  <input type="radio" name={`save-${step.id}`} checked={step.saveType === st.value}
                    onChange={() => update('saveType', st.value)} style={{ marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: '#374151' }}>{st.label}</div>
                    <div style={{ color: '#9ca3af', fontSize: 11 }}>{st.desc}</div>
                  </div>
                </label>
              ))}
              {step.saveType === 'user_trait' ? (
                <select value={step.saveTrait} onChange={e => update('saveTrait', e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13 }}>
                  {TRAIT_OPTIONS.map(t => <option key={t}>{t}</option>)}
                </select>
              ) : (
                <input value={step.saveVarName} onChange={e => update('saveVarName', e.target.value)}
                  placeholder="Variable name (e.g. order_id)" style={{
                    padding: '5px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13
                  }} />
              )}
            </div>
          )}
        </div>
      )}

      {/* Arrow */}
      {!isLast && (
        <div style={{ textAlign: 'center', marginTop: 4, color: '#9ca3af', fontSize: 18 }}>↓</div>
      )}
    </div>
  );
}

// ─── Workflow Editor Modal ────────────────────────────────────────────────────
function WorkflowEditor({ wf, onSave, onClose }) {
  const [data, setData] = useState(wf);
  const update = (key, val) => setData(d => ({ ...d, [key]: val }));

  const updateStep = (idx, step) => {
    const steps = data.steps.map((s, i) => i === idx ? step : s);
    update('steps', steps);
  };
  const removeStep = (idx) => update('steps', data.steps.filter((_, i) => i !== idx));
  const addStep = () => {
    if (data.steps.length < 7) update('steps', [...data.steps, emptyStep()]);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto'
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: 24
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            {wf.name ? `Edit: ${wf.name}` : 'New Workflow'}
          </h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
        </div>

        {/* Name */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Workflow Name *
          </label>
          <input value={data.name} onChange={e => update('name', e.target.value)}
            placeholder="e.g. Lead Qualification, Order Status" style={{
              width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
              fontSize: 14, boxSizing: 'border-box'
            }} />
        </div>

        {/* Trigger */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Trigger
          </label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TRIGGERS.map(t => (
              <button key={t.value} onClick={() => update('trigger', t.value)} style={{
                padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                border: `1.5px solid ${data.trigger === t.value ? '#25D366' : '#e0e0e0'}`,
                background: data.trigger === t.value ? '#e8fdf2' : '#fff',
                color: data.trigger === t.value ? '#128C7E' : '#555', cursor: 'pointer'
              }}>{t.label}</button>
            ))}
          </div>
          {data.trigger === 'keyword' && (
            <input value={data.keyword} onChange={e => update('keyword', e.target.value)}
              placeholder="Trigger keyword (exact match)" style={{
                marginTop: 8, width: '100%', padding: '7px 12px', border: '1px solid #e0e0e0',
                borderRadius: 8, fontSize: 13, boxSizing: 'border-box'
              }} />
          )}
        </div>

        {/* Steps */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            Steps ({data.steps.length} / 7 max)
          </div>
          {data.steps.map((step, idx) => (
            <StepEditor key={step.id} step={step} idx={idx}
              onChange={(s) => updateStep(idx, s)}
              onRemove={() => removeStep(idx)}
              isLast={idx === data.steps.length - 1} />
          ))}
          {data.steps.length < 7 && (
            <button onClick={addStep} style={{
              width: '100%', padding: '10px', border: '1.5px dashed #25D366', borderRadius: 10,
              background: '#f0fff4', color: '#128C7E', fontWeight: 600, fontSize: 13, cursor: 'pointer'
            }}>+ Add Step</button>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          <button onClick={onClose} style={{
            padding: '9px 20px', border: '1px solid #e0e0e0', borderRadius: 8,
            background: '#fff', color: '#555', cursor: 'pointer', fontSize: 13, fontWeight: 500
          }}>Cancel</button>
          <button onClick={() => onSave(data)} style={{
            padding: '9px 24px', border: 'none', borderRadius: 8,
            background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer'
          }}>Save Workflow</button>
        </div>
      </div>
    </div>
  );
}

// ─── Workflow Card ────────────────────────────────────────────────────────────
function WorkflowCard({ wf, onToggle, onEdit, onDelete }) {
  const triggerLabel = TRIGGERS.find(t => t.value === wf.trigger)?.label || wf.trigger;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '14px 18px', marginBottom: 10,
      display: 'flex', alignItems: 'center', gap: 12
    }}>
      {/* Toggle */}
      <button onClick={() => onToggle(wf.id)} style={{
        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: wf.active ? '#25D366' : '#ccc', position: 'relative', transition: '0.2s', flexShrink: 0
      }}>
        <span style={{
          position: 'absolute', top: 3, left: wf.active ? 22 : 3, width: 18, height: 18,
          borderRadius: '50%', background: '#fff', transition: '0.2s', display: 'block'
        }} />
      </button>

      {/* Info */}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{wf.name || 'Untitled Workflow'}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge>⚡ {triggerLabel}</Badge>
          {wf.trigger === 'keyword' && wf.keyword && <Badge color="#eff6ff" textColor="#1d4ed8" border="#bfdbfe">"{wf.keyword}"</Badge>}
          <Badge color="#f5f3ff" textColor="#6d28d9" border="#ddd6fe">{wf.steps.length} step{wf.steps.length !== 1 ? 's' : ''}</Badge>
        </div>
      </div>

      {/* Actions */}
      <button onClick={() => onEdit(wf)} style={{
        padding: '6px 14px', border: '1px solid #e0e0e0', borderRadius: 8,
        background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer'
      }}>✏️ Edit</button>
      <button onClick={() => onDelete(wf.id)} style={{
        padding: '6px 10px', border: '1px solid #fee2e2', borderRadius: 8,
        background: '#fff', color: '#ef4444', fontSize: 12, cursor: 'pointer'
      }}>🗑</button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const SAMPLE_WORKFLOWS = [
  {
    id: uid(), name: 'Lead Qualification', trigger: 'first_message', keyword: '', active: true,
    steps: [
      { ...emptyStep(), responseType: 'text', message: 'Hi {{customer_name}}! Welcome. What best describes your interest?', saveResponse: false },
      { ...emptyStep(), responseType: 'list', message: 'Please choose an option:', saveResponse: true, saveType: 'user_trait', saveTrait: 'company',
        listItems: [{ id: uid(), title: 'Sales Inquiry', desc: 'I want to buy' }, { id: uid(), title: 'Support', desc: 'I need help' }, { id: uid(), title: 'Partnership', desc: 'Business partnership' }] },
    ]
  },
  {
    id: uid(), name: 'Order Status Check', trigger: 'keyword', keyword: 'order', active: false,
    steps: [
      { ...emptyStep(), responseType: 'text', message: 'Please share your Order ID:', saveResponse: true, saveType: 'workflow_var', saveVarName: 'order_id' },
      { ...emptyStep(), responseType: 'webhook', webhookMethod: 'GET', webhookUrl: 'https://api.yourstore.com/order/{{order_id}}', message: '' },
    ]
  },
];

export default function Workflows() {
  const [workflows, setWorkflows] = useState(SAMPLE_WORKFLOWS);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = workflows.filter(w =>
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    (w.keyword || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = (id) =>
    setWorkflows(ws => ws.map(w => w.id === id ? { ...w, active: !w.active } : w));

  const handleSave = (wf) => {
    setWorkflows(ws => ws.some(w => w.id === wf.id) ? ws.map(w => w.id === wf.id ? wf : w) : [...ws, wf]);
    setEditing(null);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this workflow?')) setWorkflows(ws => ws.filter(w => w.id !== id));
  };

  return (
    <div>
      {/* Info banner */}
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        padding: '10px 14px', fontSize: 13, color: '#1e40af', marginBottom: 16
      }}>
        ℹ️ Workflows let you ask <strong>sequential questions</strong>, capture responses in <strong>User Traits</strong> or <strong>Workflow Variables</strong>, and integrate external APIs via webhooks. Max <strong>7 steps</strong> per workflow.
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search workflows…" style={{
              width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #e0e0e0',
              borderRadius: 8, fontSize: 13, boxSizing: 'border-box'
            }} />
        </div>
        <span style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
          {workflows.length} workflow{workflows.length !== 1 ? 's' : ''}
        </span>
        <button onClick={() => setEditing(emptyWorkflow())} style={{
          padding: '8px 16px', background: '#25D366', color: '#fff', border: 'none',
          borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap'
        }}>+ New Workflow</button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 20px', color: '#9ca3af',
          background: '#f9fafb', borderRadius: 12, border: '1.5px dashed #e5e7eb'
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔄</div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>No workflows yet</div>
          <div style={{ fontSize: 13 }}>Create your first workflow to automate multi-step conversations.</div>
        </div>
      ) : (
        filtered.map(wf => (
          <WorkflowCard key={wf.id} wf={wf}
            onToggle={handleToggle}
            onEdit={setEditing}
            onDelete={handleDelete} />
        ))
      )}

      {/* Editor Modal */}
      {editing && (
        <WorkflowEditor wf={editing} onSave={handleSave} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}